'use client';

/** Button with SemanticIcon rendering baked in, so consumers pass icon names directly without a renderIconFn. */

import * as React from 'react';
import { Button as SharedButton } from './Button.tsx';
import { SemanticIcon } from '../icons/SemanticIcon.tsx';
import { isSemanticIconName } from '../icons/registry.tsx';
import type { ButtonWithIconProps } from '@federa/types/ui-button';
import type { ComponentSize } from '@federa/types/ui-design-tokens';
import type { ButtonIconName } from '@federa/types/ui-icons';

/** `Button` hands every icon name through as the OPEN `ButtonIconName`, so the SemanticIcon vocabulary is narrowed at
 *  this boundary by `isSemanticIconName` rather than asserted: an unknown name renders no icon instead of resolving to
 *  an undefined component. `Button.renderIcon` already returns `null` when no renderer is supplied, so the caller's
 *  contract is unchanged. */
const renderSemanticIcon = (name: ButtonIconName, size: ComponentSize) => {
	const iconSizeMap: Record<ComponentSize, ComponentSize> = {
		sm: 'sm',
		md: 'md',
		lg: 'md',
	};

	if (!isSemanticIconName(name)) return null;
	return <SemanticIcon name={name} size={iconSizeMap[size]} color="inherit" />;
};

export const ButtonWithIcon = React.forwardRef<HTMLButtonElement, ButtonWithIconProps>((props, ref) => {
	return <SharedButton ref={ref} renderIconFn={renderSemanticIcon} {...props} />;
});

ButtonWithIcon.displayName = 'ButtonWithIcon';
